import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { AccessLevel, FeatureAccessMap } from './feature-access.constants';
import { RestService } from './rest.service';

@Injectable({
  providedIn: 'root'
})
export class FeatureAccessService {
  private accessMap$ = new BehaviorSubject<FeatureAccessMap | null>(null);
  private nonProjectAccessMap$ = new BehaviorSubject<FeatureAccessMap | null>(null);
 
  constructor(
    private restService: RestService
  ) {}

  loadProjectAccess(projectId: string): Observable<FeatureAccessMap> {
    this.accessMap$.next(null); // Mark as loading

    return this.restService.get('userAccess', projectId).pipe(
      map((res: any) => {
        if (res && res.operationStatus === 'SUCCESS' && res.data && res.data.featureAccessMap) {
          return res.data.featureAccessMap;
        }
        return {};
      }),
      catchError(err => {
        console.error('Error fetching feature access', err);
        return of({}); // Fail closed by returning empty map
      }),
      tap((accessMap: any) => this.accessMap$.next(accessMap as FeatureAccessMap))
    );
  }

  clearAccess() {
    this.accessMap$.next(null);
  }

  loadNonProjectAccess(): Observable<FeatureAccessMap> {
    this.nonProjectAccessMap$.next(null); // Mark as loading

    return this.restService.get('nonProjectAccess', '').pipe(
      map((res: any) => {
        if (res && res.operationStatus === 'SUCCESS' && res.data) {
          return res.data;
        }
        return {};
      }),
      catchError(err => {
        console.error('Error fetching non-project feature access', err);
        return of({}); // Fail closed by returning empty map
      }),
      tap((accessMap: any) => this.nonProjectAccessMap$.next(accessMap as FeatureAccessMap))
    );
  }

  clearNonProjectAccess() {
    this.nonProjectAccessMap$.next(null);
  }

  get accessMap(): Observable<FeatureAccessMap | null> {
    return this.accessMap$.asObservable();
  }

  get isLoaded(): boolean {
    return this.accessMap$.getValue() !== null;
  }

  get isLoaded$(): Observable<boolean> {
    return this.accessMap$.pipe(map(m => m !== null));
  }

  getAccess(featureCode: string): AccessLevel {
    const map = this.accessMap$.getValue();
    if (!map) return 'HIDDEN'; // Hide everything while loading
    return map[featureCode] || 'HIDDEN'; // Default to HIDDEN if not found (fail closed)
  }

  isVisible(featureCode: string): boolean {
    return this.getAccess(featureCode) !== 'HIDDEN';
  }

  isNavClickable(featureCode: string): boolean {
    const level = this.getAccess(featureCode);
    return level === 'WRITE' || level === 'READ_ONLY';
  }

  isPageAllowed(featureCode: string): boolean {
    const level = this.getAccess(featureCode);
    return level === 'WRITE' || level === 'READ_ONLY';
  }

  isWritable(featureCode: string): boolean {
    return this.getAccess(featureCode) === 'WRITE';
  }

  get nonProjectAccessMap(): Observable<FeatureAccessMap | null> {
    return this.nonProjectAccessMap$.asObservable();
  }

  get isNonProjectLoaded$(): Observable<boolean> {
    return this.nonProjectAccessMap$.pipe(map(m => m !== null));
  }

  getNonProjectAccess(featureCode: string): AccessLevel {
    const map = this.nonProjectAccessMap$.getValue();
    if (!map) return 'HIDDEN'; // Hide everything while loading
    return map[featureCode] || 'HIDDEN'; // Default to HIDDEN if not found (fail closed)
  }

  isNonProjectVisible(featureCode: string): boolean {
    return this.getNonProjectAccess(featureCode) !== 'HIDDEN';
  }

  isNonProjectNavClickable(featureCode: string): boolean {
    const level = this.getNonProjectAccess(featureCode);
    return level === 'WRITE' || level === 'READ_ONLY';
  }

  isNonProjectPageAllowed(featureCode: string): boolean {
    const level = this.getNonProjectAccess(featureCode);
    return level === 'WRITE' || level === 'READ_ONLY';
  }

  isNonProjectWritable(featureCode: string): boolean {
    return this.getNonProjectAccess(featureCode) === 'WRITE';
  }
}
